import React, { useState, useEffect, useRef, useCallback } from 'react';

interface MinistryTransitionsStepProps {
  data?: any;
  onComplete: (data: any) => void;
  onNext: () => void;
  onPrevious: () => void;
}

interface PositionData {
  churchName: string;
  role: string;
  datesServed: string;
  isCurrentPosition: boolean;
  howConcluded: string;
  howConcludedOther: string;
  unresolvedConflict: string;
  conflictDescription: string;
  leadershipSpeakFavorably: string;
}

const emptyPosition: PositionData = {
  churchName: '',
  role: '',
  datesServed: '',
  isCurrentPosition: false,
  howConcluded: '',
  howConcludedOther: '',
  unresolvedConflict: '',
  conflictDescription: '',
  leadershipSpeakFavorably: '',
};

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

const conclusionOptions = [
  { value: '', label: 'Select one...' },
  { value: 'called_new_position', label: 'I was called to a new ministry position' },
  { value: 'fulfilled_purpose', label: 'I felt I had fulfilled my purpose in that role' },
  { value: 'relocated', label: 'I relocated for personal or family reasons' },
  { value: 'mutual_agreement', label: 'It was a mutual decision to transition' },
  { value: 'asked_to_leave', label: 'I was asked to step down or was let go' },
  { value: 'church_closed', label: 'The church closed or dissolved' },
  { value: 'other', label: 'Other' },
];

const MinistryTransitionsStep: React.FC<MinistryTransitionsStepProps> = ({
  data,
  onComplete,
  onNext,
  onPrevious,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasBeenSubmitted, setHasBeenSubmitted] = useState(false);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  const [hasServedOnStaff, setHasServedOnStaff] = useState<string>('');
  const [ministryBackground, setMinistryBackground] = useState('');
  const [position1, setPosition1] = useState<PositionData>({ ...emptyPosition });
  const [position2, setPosition2] = useState<PositionData>({ ...emptyPosition });
  const [onlyOnePosition, setOnlyOnePosition] = useState(false);
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [confirmed, setConfirmed] = useState(false);

  const autoSaveTimer = useRef<NodeJS.Timeout | null>(null);
  const savedIndicatorTimer = useRef<NodeJS.Timeout | null>(null);
  const hasPendingChanges = useRef(false);
  const initialDataLoaded = useRef(false);

  // Build the form payload from current state
  const buildPayload = useCallback(() => ({
    hasServedOnStaff: hasServedOnStaff === 'yes',
    ministryBackground,
    positions: hasServedOnStaff === 'yes'
      ? [position1, ...(onlyOnePosition ? [] : [position2])]
      : [],
    onlyOnePosition,
    additionalInfo,
    confirmed,
  }), [hasServedOnStaff, ministryBackground, position1, position2, onlyOnePosition, additionalInfo, confirmed]);

  // Keep a ref for beforeunload
  const latestPayload = useRef(buildPayload());
  useEffect(() => {
    latestPayload.current = buildPayload();
  }, [buildPayload]);

  // Populate from existing data
  useEffect(() => {
    if (data && !initialDataLoaded.current) {
      initialDataLoaded.current = true;
      if (data.hasServedOnStaff === true) setHasServedOnStaff('yes');
      else if (data.hasServedOnStaff === false && data.submittedAt) setHasServedOnStaff('no');
      if (data.ministryBackground) setMinistryBackground(data.ministryBackground);
      if (data.positions?.[0]) setPosition1({ ...emptyPosition, ...data.positions[0] });
      if (data.positions?.[1]) setPosition2({ ...emptyPosition, ...data.positions[1] });
      if (data.onlyOnePosition) setOnlyOnePosition(true);
      if (data.additionalInfo) setAdditionalInfo(data.additionalInfo);
      if (data.confirmed) setConfirmed(data.confirmed);
      if (data.submittedAt || data.completed) setHasBeenSubmitted(true);
    }
  }, [data]);

  // Debounced auto-save
  const autoSaveDraft = useCallback(() => {
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    hasPendingChanges.current = true;

    autoSaveTimer.current = setTimeout(async () => {
      // Only auto-save if the user has selected staff status
      if (!hasServedOnStaff) return;

      setSaveStatus('saving');
      try {
        const { portalCandidateService } = await import('../../api/portalCandidateService');
        await portalCandidateService.submitMinistryTransitions({ ...latestPayload.current, draft: true });
        hasPendingChanges.current = false;
        setSaveStatus('saved');
        if (savedIndicatorTimer.current) clearTimeout(savedIndicatorTimer.current);
        savedIndicatorTimer.current = setTimeout(() => setSaveStatus('idle'), 3000);
      } catch (err) {
        console.error('Error auto-saving Ministry Transitions:', err);
        setSaveStatus('error');
      }
    }, 1500);
  }, [hasServedOnStaff]);

  // Trigger auto-save on any form change
  useEffect(() => {
    if (initialDataLoaded.current && hasServedOnStaff) {
      autoSaveDraft();
    }
  }, [hasServedOnStaff, ministryBackground, position1, position2, onlyOnePosition, additionalInfo, confirmed, autoSaveDraft]);

  // Save on window/tab close
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (hasPendingChanges.current) {
        const token = localStorage.getItem('token');
        const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
        fetch(`${apiBase}/portal-candidates/onboarding/ministry-transitions`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ ...latestPayload.current, draft: true }),
          keepalive: true,
        }).catch(() => {});
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Flush on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
      if (savedIndicatorTimer.current) clearTimeout(savedIndicatorTimer.current);
      if (hasPendingChanges.current && hasServedOnStaff) {
        const flushPayload = latestPayload.current;
        import('../../api/portalCandidateService').then(({ portalCandidateService }) => {
          portalCandidateService.submitMinistryTransitions({ ...flushPayload, draft: true }).catch(() => {});
        });
      }
    };
  }, [hasServedOnStaff]);

  const updatePosition = (posNum: 1 | 2, field: keyof PositionData, value: string) => {
    const setter = posNum === 1 ? setPosition1 : setPosition2;
    setter(prev => ({ ...prev, [field]: value }));
  };

  const validate = (): boolean => {
    if (!hasServedOnStaff) {
      setError('Please indicate whether you have served on a church or ministry staff.');
      return false;
    }

    if (hasServedOnStaff === 'no') {
      if (!ministryBackground.trim()) {
        setError('Please describe your ministry involvement.');
        return false;
      }
      if (!confirmed) {
        setError('Please confirm the accuracy of your responses.');
        return false;
      }
      return true;
    }

    // Validate position 1 - basic fields always required
    if (!position1.churchName.trim() || !position1.role.trim() || !position1.datesServed.trim()) {
      setError('Please fill in all required fields for your most recent position.');
      return false;
    }
    // Departure questions only required for past positions
    if (!position1.isCurrentPosition) {
      if (!position1.howConcluded) {
        setError('Please select how your most recent position concluded.');
        return false;
      }
      if (position1.howConcluded === 'other' && !position1.howConcludedOther.trim()) {
        setError('Please describe how your most recent position concluded.');
        return false;
      }
      if (!position1.unresolvedConflict) {
        setError('Please indicate whether there is unresolved conflict for your most recent position.');
        return false;
      }
      if (position1.unresolvedConflict === 'yes' && !position1.conflictDescription.trim()) {
        setError('Please describe the unresolved conflict for your most recent position.');
        return false;
      }
      if (!position1.leadershipSpeakFavorably) {
        setError('Please indicate whether leadership would speak favorably for your most recent position.');
        return false;
      }
    }

    // Validate position 2 if not skipped
    if (!onlyOnePosition) {
      if (!position2.churchName.trim() || !position2.role.trim() || !position2.datesServed.trim()) {
        setError('Please fill in all required fields for your second position, or check "I\'ve only held one staff position."');
        return false;
      }
      // Departure questions only required for past positions
      if (!position2.isCurrentPosition) {
        if (!position2.howConcluded) {
          setError('Please select how your second position concluded.');
          return false;
        }
        if (position2.howConcluded === 'other' && !position2.howConcludedOther.trim()) {
          setError('Please describe how your second position concluded.');
          return false;
        }
        if (!position2.unresolvedConflict) {
          setError('Please indicate whether there is unresolved conflict for your second position.');
          return false;
        }
        if (position2.unresolvedConflict === 'yes' && !position2.conflictDescription.trim()) {
          setError('Please describe the unresolved conflict for your second position.');
          return false;
        }
        if (!position2.leadershipSpeakFavorably) {
          setError('Please indicate whether leadership would speak favorably for your second position.');
          return false;
        }
      }
    }

    if (!confirmed) {
      setError('Please confirm the accuracy of your responses.');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!validate()) return;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    hasPendingChanges.current = false;
    setIsSubmitting(true);
    try {
      await onComplete(buildPayload());
      setHasBeenSubmitted(true);
      setSaveStatus('saved');
      if (savedIndicatorTimer.current) clearTimeout(savedIndicatorTimer.current);
      savedIndicatorTimer.current = setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to save. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render a single position form
  const renderPositionForm = (pos: PositionData, posNum: 1 | 2, label: string) => (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 space-y-4">
      <h3 className="text-md font-semibold text-gray-900">{label}</h3>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Church / Organization Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={pos.churchName}
            onChange={(e) => updatePosition(posNum, 'churchName', e.target.value)}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            placeholder="e.g. First Baptist Church"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Your Role / Title <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={pos.role}
            onChange={(e) => updatePosition(posNum, 'role', e.target.value)}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            placeholder="e.g. Associate Pastor"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {pos.isCurrentPosition ? 'Start Date' : 'Approximate Dates Served'} <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={pos.datesServed}
          onChange={(e) => updatePosition(posNum, 'datesServed', e.target.value)}
          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
          placeholder={pos.isCurrentPosition ? 'e.g. June 2019' : 'e.g. June 2019 – March 2023'}
        />
      </div>

      {/* Current position toggle — only for the most recent position */}
      {posNum === 1 && (
        <div className="flex items-center">
          <input
            id={`currentPosition-${posNum}`}
            type="checkbox"
            checked={pos.isCurrentPosition}
            onChange={(e) => {
              setPosition1(prev => ({
                ...prev,
                isCurrentPosition: e.target.checked,
                // Clear departure fields when toggling to current
                ...(e.target.checked ? { howConcluded: '', howConcludedOther: '', unresolvedConflict: '', conflictDescription: '', leadershipSpeakFavorably: '' } : {}),
              }));
            }}
            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <label htmlFor={`currentPosition-${posNum}`} className="ml-2 text-sm text-gray-700">
            I am currently serving in this role
          </label>
        </div>
      )}

      {/* Departure questions — only shown for past positions */}
      {!pos.isCurrentPosition && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              How did your time there conclude? <span className="text-red-500">*</span>
            </label>
            <select
              value={pos.howConcluded}
              onChange={(e) => updatePosition(posNum, 'howConcluded', e.target.value)}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            >
              {conclusionOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {pos.howConcluded === 'other' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Please describe <span className="text-red-500">*</span>
              </label>
              <textarea
                rows={2}
                value={pos.howConcludedOther}
                onChange={(e) => updatePosition(posNum, 'howConcludedOther', e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Is there any unresolved conflict, grievance, or church discipline matter related to your departure? <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-4 mt-1">
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  name={`conflict-${posNum}`}
                  value="no"
                  checked={pos.unresolvedConflict === 'no'}
                  onChange={() => updatePosition(posNum, 'unresolvedConflict', 'no')}
                  className="text-primary-600 focus:ring-primary-500"
                />
                <span className="ml-2 text-sm text-gray-700">No</span>
              </label>
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  name={`conflict-${posNum}`}
                  value="yes"
                  checked={pos.unresolvedConflict === 'yes'}
                  onChange={() => updatePosition(posNum, 'unresolvedConflict', 'yes')}
                  className="text-primary-600 focus:ring-primary-500"
                />
                <span className="ml-2 text-sm text-gray-700">Yes</span>
              </label>
            </div>
          </div>

          {pos.unresolvedConflict === 'yes' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Please describe briefly <span className="text-red-500">*</span>
              </label>
              <textarea
                rows={3}
                value={pos.conflictDescription}
                onChange={(e) => updatePosition(posNum, 'conflictDescription', e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                placeholder="Please share what happened and the current state of the situation..."
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Would the senior pastor or board chair speak favorably of your departure? <span className="text-red-500">*</span>
            </label>
            <div className="flex flex-wrap gap-4 mt-1">
              {[
                { value: 'yes', label: 'Yes' },
                { value: 'no', label: 'No' },
                { value: 'unsure', label: 'Unsure' },
                { value: 'na', label: 'N/A — they are no longer there' },
              ].map((opt) => (
                <label key={opt.value} className="inline-flex items-center">
                  <input
                    type="radio"
                    name={`favorable-${posNum}`}
                    value={opt.value}
                    checked={pos.leadershipSpeakFavorably === opt.value}
                    onChange={() => updatePosition(posNum, 'leadershipSpeakFavorably', opt.value)}
                    className="text-primary-600 focus:ring-primary-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">{opt.label}</span>
                </label>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );

  // Save status indicator
  const renderSaveIndicator = () => {
    if (saveStatus === 'saving') {
      return (
        <span className="inline-flex items-center text-xs text-gray-400">
          <svg className="animate-spin mr-1 h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          Saving...
        </span>
      );
    }
    if (saveStatus === 'saved') {
      return (
        <span className="inline-flex items-center text-xs text-green-500">
          <svg className="mr-1 h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          All changes saved
        </span>
      );
    }
    if (saveStatus === 'error') {
      return <span className="inline-flex items-center text-xs text-red-400">Auto-save failed</span>;
    }
    return null;
  };

  return (
    <div className="p-6">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Ministry Transitions</h2>
          <p className="text-gray-600">
            We believe transparency and accountability are foundational to healthy church planting.
            Understanding how previous ministry relationships concluded helps us support you well and
            ensures there are no unresolved matters that could hinder your future ministry.
          </p>
        </div>
        <div className="flex-shrink-0 pt-1">{renderSaveIndicator()}</div>
      </div>

      {hasBeenSubmitted && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-green-800 font-medium">Your responses have been saved.</p>
              <p className="text-xs text-green-600 mt-0.5">You can update your answers below at any time.</p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          {/* Staff history question */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              Have you previously served in a paid or official staff role at a church or ministry organization? <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-6">
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  name="hasServedOnStaff"
                  value="yes"
                  checked={hasServedOnStaff === 'yes'}
                  onChange={() => setHasServedOnStaff('yes')}
                  className="text-primary-600 focus:ring-primary-500"
                />
                <span className="ml-2 text-sm text-gray-700">Yes</span>
              </label>
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  name="hasServedOnStaff"
                  value="no"
                  checked={hasServedOnStaff === 'no'}
                  onChange={() => setHasServedOnStaff('no')}
                  className="text-primary-600 focus:ring-primary-500"
                />
                <span className="ml-2 text-sm text-gray-700">No</span>
              </label>
            </div>
          </div>

          {/* No staff experience path */}
          {hasServedOnStaff === 'no' && (
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Please briefly describe your ministry involvement to date <span className="text-red-500">*</span>
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Even without a formal staff role, share how you've been involved in ministry — volunteer leadership,
                small group leadership, missions work, etc.
              </p>
              <textarea
                rows={5}
                value={ministryBackground}
                onChange={(e) => setMinistryBackground(e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                placeholder="Describe your ministry background..."
              />
            </div>
          )}

          {/* Staff experience path */}
          {hasServedOnStaff === 'yes' && (
            <>
              {renderPositionForm(position1, 1, 'Most Recent Staff Position')}

              <div className="flex items-center">
                <input
                  id="onlyOnePosition"
                  type="checkbox"
                  checked={onlyOnePosition}
                  onChange={(e) => setOnlyOnePosition(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <label htmlFor="onlyOnePosition" className="ml-2 text-sm text-gray-700">
                  I've only held one staff position
                </label>
              </div>

              {!onlyOnePosition && renderPositionForm(position2, 2, 'Second Most Recent Staff Position')}

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Is there anything else about your ministry history you believe we should know?
                </label>
                <textarea
                  rows={3}
                  value={additionalInfo}
                  onChange={(e) => setAdditionalInfo(e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  placeholder="(Optional) Share any additional context..."
                />
              </div>
            </>
          )}

          {/* Confirmation checkbox - shown once they've picked a path */}
          {hasServedOnStaff && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <label className="flex items-start">
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  className="h-4 w-4 mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="ml-3 text-sm text-gray-700">
                  I confirm that the information above is accurate and complete to the best of my knowledge.
                  I understand that 242 Network may contact the churches listed above as part of the assessment process.
                </span>
              </label>
            </div>
          )}
        </div>

        {error && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}

        <div className="mt-8 flex justify-between">
          <button
            type="button"
            onClick={onPrevious}
            disabled={isSubmitting}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
          >
            Previous
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Saving...
              </>
            ) : hasBeenSubmitted ? (
              'Update & Continue'
            ) : (
              'Save & Continue'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default MinistryTransitionsStep;
